import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useRegisterMutation } from "../../../features/auth/authApiSlice";
import { storageService } from "../../../services/storage-services";
import { FailedNotify, SuccessNotify } from "../../../constants/tostify";
import buildSignupPayload from "./userNormalizer";
import { PLACEHOLDER_EDUCATION } from "../../../constants/auth/sign-up";

// Maps RTK Query error shapes to a user-friendly message
function exactErrorMessage(error) {
  const status =
    error?.status ?? error?.originalStatus ?? error?.error?.status ?? null;

  const messages =
    error?.data?.message ??
    error?.data?.error ??
    error?.error ??
    error?.message ??
    null;

  const MAP = {
    422: messages || "This account already exists.",
    400: messages || "Please check your input and try again.",
    401: messages || "Unauthorized.",
    500: "Server error. Please try again later.",
    FETCH_ERROR: "Network error. Please check your connection.",
    PARSING_ERROR: "Unexpected server response. Please try again.",
  };

  return MAP[status] ?? messages ?? "Something went wrong. Please try again.";
}

export const useSignupSubmit = (userData, locationExtra) => {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [register, { isLoading }] = useRegisterMutation();

  const handleSubmit = async (e) => {
    e?.preventDefault();

    /* Build payload via shared utility — no inline normalization */
    const payload = buildSignupPayload({
      ...userData,
      latitude: locationExtra?.latitude,
      longitude: locationExtra?.longitude,
      clientIp: locationExtra?.clientIp,
      // filter placeholder education entries before sending
      education: (userData.education ?? []).filter((edu) =>
        edu && !PLACEHOLDER_EDUCATION.includes(typeof edu === "string" ? edu : edu?.text),
      ),
    });

    try {
      const response = await register(payload).unwrap();

      if (!response?.success) {
        FailedNotify(response?.message || "Registration Failed");
        return;
      }

      const { _id, email } = response.data ?? {};

      // Persist minimal identifiers only
      if (_id) storageService.set("userid", _id);
      if (email) storageService.set("user", email);

      setIsRedirecting(true);
      SuccessNotify("Registered successfully!");

      navigate(`/verify-code/${email ?? userData.email}`);
    } catch (err) {
      console.error("[useSignupSubmit] Registration error:", err);
      FailedNotify(exactErrorMessage(err));
    }
  };

  // Expose cleanup so parent can call it in useEffect cleanup
  const cancelNavigation = () => clearTimeout(timerRef.current);

  return { handleSubmit, isLoading, isRedirecting, cancelNavigation };
};
